import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import Iframe from 'react-iframe';
import { ModalBody, Spinner, Row, Col } from 'reactstrap';
import { useTranslation } from 'react-i18next';

import useApi from 'hooks/useApi';
import { setCalendarAccount } from 'redux/actions/calendarActions';
import { addBreadcrumb } from 'redux/actions/layoutActions';

import { ReactComponent as CalendarCheckIcon } from 'assets/icon-calendar-check.svg';
import { ReactComponent as CalendarIcon } from 'assets/icon-calendar.svg';
import { ReactComponent as RefreshIcon } from 'assets/icon-rotate.svg';
import { ReactComponent as AddIcon } from 'assets/icon-add-round.svg';

import { ModalHeader } from 'shared/components/modal/ModalHeaders';

import { LabelIcon } from 'shared/components/Labels';
import { Button, ButtonLabelIcon } from 'shared/components/Buttons';
import { colors } from 'shared/defaultTheme';
import { createToken } from 'shared/utils';

import { CalendarBackground, CalendarFormModal } from './styles';

const Calendar = () => {
  const { accountApi, lexonApi } = useApi();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const theme = useSelector((state) => state.theme);
  const { entradaUsuario, idApp } = useSelector((state) => state.auth);
  const { calendarAccount } = useSelector((state) => state.calendar);
  const { mainColor } = theme;
  const [openFormModal, setOpenFormModal] = useState(false);
  const [indexCalender, setIndexCalendar] = useState(0);
  const [calendarUrl, setCalendarUrl] = useState('');
  const [calendarEventUrl, setCalendarEventUrl] = useState('');

  const [, getAccountsDefault] = accountApi(
    {
      url: `accounts/auth/usermail/${entradaUsuario}/account/default`,
      method: 'GET',
    },
    { manual: true }
  );

  const [{ loading: tokenLexonLoading }, getTokenLexon] = lexonApi(
    {
      method: 'PUT',
    },
    { manual: true }
  );

  useEffect(() => {
    dispatch(
      addBreadcrumb({
        label: 'Settings_calendar',
        active: true,
        link: pathname,
        isModify: false,
      })
    );
  }, []);

  const loadToken = () => {
    const newData = {
      idClienteNavision: entradaUsuario,
      idApp,
      appConnectUrl: window.location.origin,
    };
    return createToken(newData, window.REACT_APP_TOKEN_ACCOUNT);
  };

  const loadAccount = async () => {
    try {
      const {
        data: { data },
      } = await getAccountsDefault();
      const calendarConfig = data.configCalendar ? data.configCalendar : data;
      dispatch(setCalendarAccount(calendarConfig));
      const tokenData = loadToken();
      switch (calendarConfig.provider) {
        case 'GOOGLE':
          setCalendarUrl(
            `${window.PATH_CL_GOO}/calendar/access/${tokenData}/?prov=GO0&email=${
              calendarConfig.account ? calendarConfig.account : calendarConfig.email
            }`
          );
          break;
        case 'OUTLOOK':
          setCalendarUrl(`${window.PATH_CL_OUT}/calendar/access/${tokenData}/?prov=OU0`);
          break;
        case 'IMAP':
          setCalendarUrl(`${window.PATH_CL_IMAP}/calendar/access/${tokenData}/?prov=IM0`);
          break;
        default:
          break;
      }
    } catch (error) {
      console.error('error load account:', error);
      dispatch(setCalendarAccount(null));
    }
  };

  useEffect(() => {
    loadAccount();
  }, []);

  const onRefreshCalendar = () => {
    setIndexCalendar(indexCalender + 1);
  };

  useEffect(() => {
    window.addEventListener('message', (event) => {
      const type = typeof event.data;
      try {
        if (type === 'string') {
          const data = JSON.parse(event.data);
          if (data.isLogout) {
            if (calendarAccount.provider === 'GOOGLE') {
              dispatch(setCalendarAccount(null));
            }
            if (calendarAccount.provider === 'OUTLOOK') {
              dispatch(setCalendarAccount(null));
            }
          }
        }
        if (type === 'string' && openFormModal) {
          const data = JSON.parse(event.data);
          if (data.actionCancelled) {
            setOpenFormModal(false);
            onRefreshCalendar();
          }
        }
      } catch (error) {
        // console.error(error);
      }
    });
    return () => {
      window.removeEventListener('message', {});
    };
  }, [openFormModal]);

  const handleOpenModal = () => {
    const data = loadToken();
    switch (calendarAccount.provider) {
      case 'GOOGLE':
        setCalendarEventUrl(
          `${window.PATH_CL_GOO}/calendar/?prov=GO0&email=${
            calendarAccount.account ? calendarAccount.account : calendarAccount.email
          }&appConnectEvent=${data}`
        );
        break;
      case 'OUTLOOK':
        setCalendarEventUrl(`${window.PATH_CL_OUT}/calendar/?prov=OU0&appConnectEvent=${data}`);
        break;
      case 'IMAP':
        setCalendarEventUrl(`${window.PATH_CL_IMAP}/calendar/?prov=IM0&appConnectEvent=${data}`);
        break;
      default:
        break;
    }
    setOpenFormModal(true);
  };

  const onCloseFormModal = () => {
    setOpenFormModal(false);
  };

  const onConfigAccount = async () => {
    try {
      const {
        data: { data },
      } = await getTokenLexon({
        data: {
          idClienteNavision: entradaUsuario,
        },
        url: `utilsauth/Lexon/token/lexon?addTerminatorToToken=true`,
      });
      window.open(`${window.PATH_CL_POR}/access/${data.token}/`, '_blank');
    } catch (error) {
      console.error('error lexon: ', error);
    }
  };

  const Title = () => {
    return (
      <Row className="row-title">
        <Col sm={12} className="ml-n3">
          <h3>{t('calendar_create_event')}</h3>
        </Col>
      </Row>
    );
  };

  return (
    <div className="pb-3">
      <CalendarBackground className="">
        <div className="d-flex top-mobile mb-4">
          <LabelIcon
            label={t('Settings_calendar')}
            Icon={() => (
              <CalendarCheckIcon className="label-icon__notification" fill={colors.white} />
            )}
            color={mainColor}
            className="ml-1 label-icon text-uppercase"
            classNameMain=""
          />
          {calendarAccount && (
            <div className="d-flex w-100 justify-content-end">
              <ButtonLabelIcon
                id="add-icon-calendar"
                text={t('general_button_new')}
                type="button"
                Icon={() => <AddIcon fill={colors.black} />}
                className="button-icon mr-1"
                onClick={handleOpenModal}
              />
              <ButtonLabelIcon
                id="refresh-icon-calendar"
                text={t('general_button_refresh')}
                type="button"
                Icon={() => <RefreshIcon fill={colors.black} />}
                className="button-icon"
                onClick={onRefreshCalendar}
              />
            </div>
          )}
        </div>
        {calendarAccount ? (
          <div>
            <Iframe
              key={indexCalender}
              url={calendarUrl}
              width="100%"
              height="741px"
              id="calendarIframe"
              frameBorder="0"
              className="frame-calendar"
              allow="fullscreen"
              display="initial"
              position="relative"
            />
          </div>
        ) : (
          <>
            {!tokenLexonLoading ? (
              <div className="d-flex justify-content-center">
                <div className="box-calendar-sync">
                  <div className="d-flex align-items-start mb-3">
                    <CalendarIcon width={20} />
                    <span className="ml-2 title-calendars-sync">
                      {t('calendar_calendars_sync')}
                    </span>
                  </div>
                  <span className="info-account">{t('calendar_no_account')}</span>
                  <div className="btn-calendar-sync mt-3 mb-3">
                    <Button
                      className="text-uppercase"
                      type="button"
                      label={t('calendar_config_account')}
                      color="primary"
                      onClick={onConfigAccount}
                    />
                  </div>
                  <div className="d-flex justify-content-center">
                    <RefreshIcon width={15} height={20} />
                    <span className="refresh-calendars-sync ml-2">
                      {t('calendar_refresh_screen')}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="d-flex justify-content-center align-items-center">
                <Spinner />
              </div>
            )}
          </>
        )}
        <CalendarFormModal isOpen={openFormModal} toggle={onCloseFormModal} size="lg">
          <ModalHeader
            className="modal-header"
            backgroundColor={colors.transparent}
            onClose={onCloseFormModal}
          />
          <ModalBody>
            <div className="calendar-event-container">
              <Title />
              {openFormModal ? (
                <Iframe
                  url={calendarEventUrl}
                  width="100%"
                  height="500px"
                  id="form-iframe"
                  frameBorder="0"
                  display="initial"
                  position="relative"
                />
              ) : null}
            </div>
          </ModalBody>
        </CalendarFormModal>
      </CalendarBackground>
    </div>
  );
};

export default Calendar;
